import { getEasternTimeParts, getPreferredUsQuoteSession } from '@/utils/usMarketSession'

function pickPositiveNumber(...values) {
  for (const value of values) {
    const numberValue = Number(value)
    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue
    }
  }
  return 0
}

export function normalizeQuoteSymbol(symbol) {
  if (!symbol) return ''
  return String(symbol).trim().toUpperCase()
    .replace(/^(US|BATS|NASDAQ|NYSE|ARCA|AMEX|IEX|HK|SH|SZ|OVERNIGHT)\./, '')
    .replace(/\.(US|BATS|HK|SH|SZ)$/, '')
}

export function toMarketOverviewCode(symbol) {
  const upper = String(symbol || '').trim().toUpperCase()
  if (!upper) return ''
  if (/^(US|HK|SH|SZ|SG|JP|BATS)\./.test(upper)) {
    return upper.startsWith('BATS.') ? `US.${normalizeQuoteSymbol(upper)}` : upper
  }
  // 后缀格式：07709.HK → HK.07709 / NVDA.US → US.NVDA
  const suffixMatch = upper.match(/^(.+)\.(US|HK|SH|SZ|BATS)$/)
  if (suffixMatch) {
    const prefix = suffixMatch[2] === 'BATS' ? 'US' : suffixMatch[2]
    return `${prefix}.${suffixMatch[1]}`
  }
  // 纯数字按位数推断市场
  const bare = normalizeQuoteSymbol(upper)
  if (/^\d{5}$/.test(bare)) return `HK.${bare}`
  if (/^\d{6}$/.test(bare)) {
    return `${bare.startsWith('6') || bare.startsWith('9') ? 'SH' : 'SZ'}.${bare}`
  }
  return `US.${bare}`
}

export function getSessionAwarePrice(quote = {}) {
  const code = String(quote.code || '').toUpperCase()
  const isUs = code.startsWith('US.') || code.startsWith('BATS.')
  if (!isUs) {
    return pickPositiveNumber(
      quote.regular?.price,
      quote.pre_market?.price,
      quote.after_hours?.price,
      quote.overnight?.price,
      quote.last_price,
      quote.latest_price
    )
  }
  const session = getPreferredUsQuoteSession()
  if (session === 'PRE') return pickPositiveNumber(quote.pre_market?.price, quote.regular?.price, quote.after_hours?.price)
  if (session === 'REGULAR') return pickPositiveNumber(quote.regular?.price, quote.pre_market?.price, quote.after_hours?.price)
  if (session === 'POST') return pickPositiveNumber(quote.after_hours?.price, quote.regular?.price, quote.overnight?.price)
  return pickPositiveNumber(quote.overnight?.price, quote.regular?.price, quote.after_hours?.price, quote.pre_market?.price)
}

export function getQuotePriceBySession(quote = {}, session = 'REGULAR') {
  if (session === 'PRE') return pickPositiveNumber(quote.pre_market?.price, quote.regular?.price)
  if (session === 'POST') return pickPositiveNumber(quote.after_hours?.price, quote.regular?.price)
  if (session === 'OVERNIGHT') return pickPositiveNumber(quote.overnight?.price, quote.after_hours?.price, quote.regular?.price)
  return pickPositiveNumber(quote.regular?.price, quote.pre_market?.price, quote.after_hours?.price)
}

export function getQuoteRegularOpenPrice(quote = {}) {
  return pickPositiveNumber(
    quote.regular?.open_price,
    quote.regular?.openPrice,
    quote.open_price,
    quote.openPrice
  )
}

export function getSessionTurnoverAverage(session = {}) {
  const turnover = Number(session?.turnover)
  const volume = Number(session?.volume)
  if (!Number.isFinite(turnover) || !Number.isFinite(volume) || !(turnover > 0) || !(volume > 0)) return 0
  return turnover / volume
}

export function getSessionTurnoverTotals(session = {}) {
  const turnover = Number(session?.turnover)
  const volume = Number(session?.volume)
  if (!Number.isFinite(turnover) || !Number.isFinite(volume) || !(turnover > 0) || !(volume > 0)) {
    return { turnover: 0, volume: 0 }
  }
  return { turnover, volume }
}

export function getUsTradingDaySessionKeys(date = new Date()) {
  const { weekday, totalMinutes } = getEasternTimeParts(date)
  if (weekday >= 1 && weekday <= 4) {
    if (totalMinutes < 4 * 60) return ['overnight']
    if (totalMinutes < 9 * 60 + 30) return ['overnight', 'pre_market']
    if (totalMinutes < 16 * 60) return ['overnight', 'pre_market', 'regular']
    if (totalMinutes < 20 * 60) return ['overnight', 'pre_market', 'regular', 'after_hours']
    return ['overnight']
  }
  if (weekday === 5) {
    if (totalMinutes < 4 * 60) return ['overnight']
    if (totalMinutes < 9 * 60 + 30) return ['overnight', 'pre_market']
    if (totalMinutes < 16 * 60) return ['overnight', 'pre_market', 'regular']
    if (totalMinutes < 20 * 60) return ['overnight', 'pre_market', 'regular', 'after_hours']
    return ['overnight', 'pre_market', 'regular', 'after_hours']
  }
  if (weekday === 6) return ['overnight', 'pre_market', 'regular', 'after_hours']
  if (totalMinutes < 20 * 60) return ['overnight', 'pre_market', 'regular', 'after_hours']
  return ['overnight']
}

export function getUsTradingDayAveragePrice(quote = {}, date = new Date()) {
  const sessionKeys = getUsTradingDaySessionKeys(date)
  let turnoverSum = 0
  let volumeSum = 0
  sessionKeys.forEach((key) => {
    const totals = getSessionTurnoverTotals(quote?.[key])
    turnoverSum += totals.turnover
    volumeSum += totals.volume
  })
  if (turnoverSum > 0 && volumeSum > 0) return turnoverSum / volumeSum
  return 0
}

export function getUsTradingDayLowPrice(quote = {}, date = new Date()) {
  const sessionKeys = getUsTradingDaySessionKeys(date)
  let minLow = 0
  sessionKeys.forEach((key) => {
    const low = Number(quote?.[key]?.low_price ?? quote?.[key]?.lowPrice)
    if (!Number.isFinite(low) || !(low > 0)) return
    if (!(minLow > 0) || low < minLow) minLow = low
  })
  return minLow > 0 ? minLow : 0
}

export function getUsTradingDayHighPrice(quote = {}, date = new Date()) {
  const sessionKeys = getUsTradingDaySessionKeys(date)
  let maxHigh = 0
  sessionKeys.forEach((key) => {
    const high = Number(quote?.[key]?.high_price ?? quote?.[key]?.highPrice)
    if (!Number.isFinite(high) || !(high > 0)) return
    if (!(maxHigh > 0) || high > maxHigh) maxHigh = high
  })
  return maxHigh > 0 ? maxHigh : 0
}

export function getQuoteAverageBySession(quote = {}, session = 'REGULAR') {
  const sessionData = session === 'PRE'
    ? quote.pre_market
    : (session === 'POST'
      ? quote.after_hours
      : (session === 'OVERNIGHT' ? quote.overnight : quote.regular))

  const sessionAvg = pickPositiveNumber(
    sessionData?.average,
    sessionData?.avg_price,
    sessionData?.avgPrice,
    sessionData?.vwap,
    sessionData?.vwap_price
  )
  if (sessionAvg > 0) return sessionAvg

  const turnoverAvg = getSessionTurnoverAverage(sessionData)
  if (turnoverAvg > 0) return turnoverAvg
  return 0
}

export function getQuoteAveragePrice(quote = {}) {
  const code = String(quote.code || '').toUpperCase()
  const isUs = code.startsWith('US.') || code.startsWith('BATS.')
  if (isUs) {
    const session = getPreferredUsQuoteSession()
    const sessionAvg = getQuoteAverageBySession(quote, session)
    if (sessionAvg > 0) return sessionAvg
  }

  const directAvg = pickPositiveNumber(
    quote.average,
    quote.avg_price,
    quote.avgPrice,
    quote.vwap,
    quote.vwap_price,
    quote.latest_average,
    quote.latestAverage
  )
  if (directAvg > 0) return directAvg

  return getSessionTurnoverAverage(quote.regular)
}

export function getQuoteRegularAmplitude(quote = {}) {
  const prevClose = Number(quote?.prev_close ?? quote?.prevClose)
  const regularHigh = Number(quote?.regular?.high_price ?? quote?.regular?.highPrice)
  const regularLow = Number(quote?.regular?.low_price ?? quote?.regular?.lowPrice)
  if (!(prevClose > 0) || !(regularHigh > 0) || !(regularLow > 0)) return 0
  return ((regularHigh - regularLow) / prevClose) * 100
}

export function getSessionAwareChangeMetrics(quote = {}) {
  const code = String(quote.code || '').toUpperCase()
  const isUs = code.startsWith('US.') || code.startsWith('BATS.')
  const toNum = (value) => {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : 0
  }
  if (!isUs) {
    const regularPrice = getQuotePriceBySession(quote, 'REGULAR')
    const prevClose = toNum(quote.prev_close ?? quote.prevClose)
    const diff = regularPrice - prevClose
    return {
      diff,
      rate: prevClose > 0 ? (diff / prevClose) * 100 : 0
    }
  }

  const session = getPreferredUsQuoteSession()
  const regularPrice = getQuotePriceBySession(quote, 'REGULAR')
  const prevClose = toNum(quote.prev_close ?? quote.prevClose)
  const regularOpenPrice = getQuoteRegularOpenPrice(quote)
  const currentPrice = getQuotePriceBySession(quote, session) || getSessionAwarePrice(quote)

  if (session === 'REGULAR') {
    const diff = regularPrice - prevClose
    return {
      diff,
      rate: prevClose > 0 ? (diff / prevClose) * 100 : 0
    }
  }

  const basePrice = regularPrice || regularOpenPrice || prevClose
  const diff = currentPrice - basePrice
  return {
    diff,
    rate: basePrice > 0 ? (diff / basePrice) * 100 : 0
  }
}

export function getSessionAwareChangeRate(quote = {}) {
  return getSessionAwareChangeMetrics(quote).rate
}

export function buildQuoteCacheEntry(rawQuote = {}, extra = {}) {
  const symbol = normalizeQuoteSymbol(extra.symbol || rawQuote?.code || rawQuote?.symbol || '')
  if (!symbol) return null
  return {
    symbol,
    code: rawQuote?.code || extra.code || toMarketOverviewCode(symbol),
    rawQuote,
    quotePrice: Number(extra.quotePrice) > 0 ? Number(extra.quotePrice) : getSessionAwarePrice(rawQuote),
    quoteAvgPrice: Number(extra.quoteAvgPrice) > 0 ? Number(extra.quoteAvgPrice) : getQuoteAveragePrice(rawQuote),
    quoteAmplitude: Number.isFinite(Number(extra.quoteAmplitude)) ? Number(extra.quoteAmplitude) : getQuoteRegularAmplitude(rawQuote),
    quoteChangeRate: Number.isFinite(Number(extra.quoteChangeRate)) ? Number(extra.quoteChangeRate) : getSessionAwareChangeRate(rawQuote),
    updatedAt: Number(extra.updatedAt) || Date.now(),
    source: extra.source || 'market-overview'
  }
}
